import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Bell, BellOff, Check } from "lucide-react";
import { useBranding } from "./Layout";

function urlBase64ToUint8(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushToggle() {
  const branding = useBranding();
  const [support, setSupport] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [hint, setHint] = useState("");

  useEffect(() => {
    (async () => {
      const hasSW = "serviceWorker" in navigator;
      const hasPush = "PushManager" in window;
      const hasNote = "Notification" in window;
      if (!hasSW || !hasPush || !hasNote) {
        setSupport(false);
        return;
      }
      try {
        const { data } = await api.get("/push/vapid-public");
        setPublicKey(data.public_key || "");
      } catch {}
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch {}
    })();
  }, []);

  const subscribe = async () => {
    setBusy(true); setHint("");
    try {
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setHint("Notifications blocked. Enable them in your browser settings to turn this on.");
        return;
      }
      if (!publicKey) {
        setHint("Push isn't configured on the server.");
        return;
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8(publicKey),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subJson.endpoint,
        keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
      });
      setSubscribed(true);
      setHint("Notifications on — we'll ping you when sellers send forms or questions.");
    } catch (e) {
      const friendly = e.response?.data?.detail
        ? (typeof e.response.data.detail === "string" ? e.response.data.detail : "Server rejected the subscription.")
        : (e.message || "Couldn't enable notifications.");
      setHint(friendly);
    } finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true); setHint("");
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        const subJson = sub.toJSON();
        try {
          await api.post("/push/unsubscribe", {
            endpoint: subJson.endpoint,
            keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
          });
        } catch {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setHint("Notifications turned off.");
    } catch (e) {
      setHint(e.message || "Couldn't turn off notifications.");
    } finally { setBusy(false); }
  };

  if (!support) {
    return (
      <div data-testid="push-unsupported" style={{ fontSize: 13, color: "var(--text-muted)" }}>
        Push isn't supported on this browser. On iPhone, add {branding.product_name} to your home screen first — then Safari supports notifications.
      </div>
    );
  }

  if (subscribed) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span data-testid="push-on" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--success)", fontSize: 13, fontWeight: 500 }}>
          <Check size={14} /> Notifications on
        </span>
        <button
          data-testid="push-disable-btn"
          onClick={unsubscribe}
          disabled={busy}
          className="btn-ghost"
          style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px" }}
        >
          <BellOff size={14} /> Turn off
        </button>
        {hint && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{hint}</span>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <button
        data-testid="push-enable-btn"
        onClick={subscribe}
        disabled={busy || permission === "denied"}
        className="btn-secondary"
        style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px" }}
      >
        <Bell size={14} /> {busy ? "Enabling…" : "Enable notifications"}
      </button>
      {permission === "denied" && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Blocked in browser settings — enable there, then try again.
        </span>
      )}
      {hint && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{hint}</span>}
    </div>
  );
}
